import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import './GlossyCard.css';
import ElectricBorder from './ElectricBorder';

interface GlossyCardProps {
    children: React.ReactNode;
    isActive: boolean;
    isElectric?: boolean;
    electricColor?: string;
}

const GlossyCard: React.FC<GlossyCardProps> = ({
    children,
    isActive,
    isElectric = false,
    electricColor = '#7df9ff'
}) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const shellRef = useRef<HTMLDivElement>(null);

    const tiltEngine = useMemo(() => {
        let rafId: number | null = null;
        let running = false;
        let lastTs = 0;

        let currentX = 0;
        let currentY = 0;
        let targetX = 0;
        let targetY = 0;

        const DEFAULT_TAU = 0.14;

        const setVarsFromXY = (x: number, y: number) => {
            const shell = shellRef.current;
            const wrap = wrapRef.current;
            if (!shell || !wrap) return;

            const width = shell.clientWidth || 1;
            const height = shell.clientHeight || 1;

            const percentX = Math.min(Math.max((100 / width) * x, 0), 100);
            const percentY = Math.min(Math.max((100 / height) * y, 0), 100);

            const centerX = percentX - 50;
            const centerY = percentY - 50;

            const adjust = (v: number, fMin: number, fMax: number, tMin: number, tMax: number): number =>
                parseFloat((tMin + ((tMax - tMin) * (v - fMin)) / (fMax - fMin)).toFixed(3));

            const properties = {
                '--pointer-x': `${percentX}%`,
                '--pointer-y': `${percentY}%`,
                '--background-x': `${adjust(percentX, 0, 100, 35, 65)}%`,
                '--background-y': `${adjust(percentY, 0, 100, 35, 65)}%`,
                '--pointer-from-center': `${Math.min(Math.max(Math.hypot(percentY - 50, percentX - 50) / 50, 0), 1)}`,
                '--pointer-from-top': `${percentY / 100}`,
                '--pointer-from-left': `${percentX / 100}`,
                '--rotate-x': `${parseFloat((-(centerX / 5)).toFixed(3))}deg`,
                '--rotate-y': `${parseFloat((centerY / 4).toFixed(3))}deg`
            } as Record<string, string>;

            for (const [k, v] of Object.entries(properties)) wrap.style.setProperty(k, v);
        };

        const step = (ts: number) => {
            if (!running) return;
            if (lastTs === 0) lastTs = ts;
            const dt = (ts - lastTs) / 1000;
            lastTs = ts;

            const k = 1 - Math.exp(-dt / DEFAULT_TAU);

            currentX += (targetX - currentX) * k;
            currentY += (targetY - currentY) * k;

            setVarsFromXY(currentX, currentY);

            const stillFar = Math.abs(targetX - currentX) > 0.05 || Math.abs(targetY - currentY) > 0.05;

            if (stillFar) {
                rafId = requestAnimationFrame(step);
            } else {
                running = false;
                lastTs = 0;
                if (rafId) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
            }
        };

        const start = () => {
            if (running) return;
            running = true;
            lastTs = 0;
            rafId = requestAnimationFrame(step);
        };

        return {
            setTarget(x: number, y: number) {
                targetX = x;
                targetY = y;
                start();
            },
            toCenter() {
                const shell = shellRef.current;
                if (!shell) return;
                this.setTarget(shell.clientWidth / 2, shell.clientHeight / 2);
            },
            cancel() {
                if (rafId) cancelAnimationFrame(rafId);
                rafId = null;
                running = false;
                lastTs = 0;
            }
        };
    }, []);

    const getOffsets = (evt: PointerEvent, el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
    };

    const handlePointerMove = useCallback(
        (event: PointerEvent) => {
            const shell = shellRef.current;
            if (!shell || !isActive) return;
            const { x, y } = getOffsets(event, shell);
            tiltEngine.setTarget(x, y);
        },
        [tiltEngine, isActive]
    );

    const handlePointerEnter = useCallback(
        (event: PointerEvent) => {
            const shell = shellRef.current;
            if (!shell || !isActive) return;

            shell.classList.add('glossy-active');

            const { x, y } = getOffsets(event, shell);
            tiltEngine.setTarget(x, y);
        },
        [tiltEngine, isActive]
    );

    const handlePointerLeave = useCallback(() => {
        const shell = shellRef.current;
        if (!shell || !isActive) return;

        tiltEngine.toCenter();
        shell.classList.remove('glossy-active');
    }, [tiltEngine, isActive]);

    useEffect(() => {
        if (!isActive) return;

        const shell = shellRef.current;
        if (!shell) return;

        const pointerMoveHandler = handlePointerMove as EventListener;
        const pointerEnterHandler = handlePointerEnter as EventListener;
        const pointerLeaveHandler = handlePointerLeave as EventListener;

        shell.addEventListener('pointerenter', pointerEnterHandler);
        shell.addEventListener('pointermove', pointerMoveHandler);
        shell.addEventListener('pointerleave', pointerLeaveHandler);

        return () => {
            shell.removeEventListener('pointerenter', pointerEnterHandler);
            shell.removeEventListener('pointermove', pointerMoveHandler);
            shell.removeEventListener('pointerleave', pointerLeaveHandler);
            tiltEngine.cancel();
            shell.classList.remove('glossy-active');
        };
    }, [isActive, tiltEngine, handlePointerMove, handlePointerEnter, handlePointerLeave]);

    // Force active state and initial tilt when isActive becomes true
    useEffect(() => {
        if (isActive) {
            const shell = shellRef.current;
            if (shell) {
                shell.classList.add('glossy-active');
                // Optional: Trigger a slight movement or reset to center to ensure vars are set
                tiltEngine.toCenter();
            }
        }
    }, [isActive, tiltEngine]);

    const cardContent = (
        <div className="glossy-card">
            {children}
            <div className="glossy-shine" />
            <div className="glossy-glare" />
        </div>
    );

    if (!isActive) {
        return (
            <div className="glossy-card-wrapper transition-all duration-300">
                {isElectric ? (
                    <ElectricBorder
                        color={electricColor}
                        speed={1}
                        chaos={0.5}
                        thickness={2}
                        style={{ borderRadius: '0.5rem', padding: '2px' }}
                    >
                        {children}
                    </ElectricBorder>
                ) : (
                    children
                )}
            </div>
        )
    }

    return (
        <div ref={wrapRef} className="glossy-card-wrapper">
            <div ref={shellRef} className={`glossy-card-shell ${isActive ? 'glossy-active' : ''}`}>
                {isElectric ? (
                    <ElectricBorder
                        color={electricColor}
                        speed={1}
                        chaos={0.5}
                        thickness={2}
                        style={{ borderRadius: '0.5rem', padding: '2px' }}
                    >
                        {cardContent}
                    </ElectricBorder>
                ) : (
                    cardContent
                )}
            </div>
        </div>
    );
};

export default GlossyCard;
